package fr.frux.shared.dto

import kotlinx.serialization.Serializable
import kotlin.time.Instant

/**
 * Le type d'événement (#225 P0, ADR 0017) — liste fermée, alignée sur le `CHECK` de `notifications`
 * (V22, étendu par V29, V67, V68, V69, V70). `HARVEST_JOINED`/`HARVEST_CANCELLED`/`HARVEST_STARTED` et, depuis
 * #581/#582/#583, `HARVEST_LEFT`/`HARVEST_FULL`/`HARVEST_COMPLETED` viennent de `HarvestService`,
 * `TREE_DISPUTED`/`REPORT_RESOLVED` de `TreeService` (et des files de signalements du back-office,
 * #588), `MATURITY_DIGEST` (#253/#157) de `MaturityDigestService`, `FEEDBACK_HANDLED` (#586) de
 * `AdminFeedbackService`, `TREE_ACTIVITY` (#584) et `FAVORITE_TREE_CHANGED` (#585) de `TreeService`
 * et `AdminTreeService`, `HARVEST_REMINDER` (#580) de `HarvestReminderService`,
 * `PREMIUM_EXPIRING`/`PREMIUM_EXPIRED` (#587) de `PremiumExpiryService`.
 *
 * [UNKNOWN] en dernier membre, jamais écrit par le backend (verrouillé par le CHECK) : c'est la
 * valeur de repli d'un client qui ne connaît pas encore un kind ajouté après sa sortie — sans elle,
 * `ignoreUnknownKeys` ne couvre pas une valeur d'enum inconnue et tout le décodage de la liste
 * échouerait (badge + écran Activité morts). Voir [NotificationDto.kind].
 */
@Serializable
enum class NotificationKindDto {
    HARVEST_JOINED,
    HARVEST_CANCELLED,
    HARVEST_STARTED,
    TREE_DISPUTED,
    REPORT_RESOLVED,
    MATURITY_DIGEST,

    /** Session révoquée après détection d'un rejeu de refresh token (#295, ADR 0019). */
    SESSION_REVOKED,

    // back-office lot 1 (#333), in-app seulement
    SPECIES_SUGGESTION_ACCEPTED,
    SPECIES_SUGGESTION_DISMISSED,

    // back-office lot 2 (#340), in-app seulement, jamais le motif
    TREE_SPECIES_CORRECTED,

    // modération de contenu (#550), in-app seulement : action et fondement, jamais le motif
    CONTENT_MODERATED,

    // récoltes (#581, #582, #583)
    HARVEST_LEFT,
    HARVEST_FULL,
    HARVEST_COMPLETED,

    // retour utilisateur traité (#586), in-app seulement
    FEEDBACK_HANDLED,

    // arbres (#584, #585) : une ligne par arbre et par jour, regroupée (ADR 0017 amendé)
    TREE_ACTIVITY,
    FAVORITE_TREE_CHANGED,

    // rappel de récolte planifiée dans les 24 h (#580)
    HARVEST_REMINDER,

    // fin d'abonnement Frux+ annoncée / constatée (#587), sujet ACCOUNT, in-app seulement
    PREMIUM_EXPIRING,
    PREMIUM_EXPIRED,
    UNKNOWN,
}

/** [UNKNOWN] : même rôle de repli que [NotificationKindDto.UNKNOWN], jamais écrit par le backend. */
@Serializable
enum class NotificationSubjectTypeDto {
    HARVEST,
    TREE,
    DIGEST,

    /** Sujet = le compte lui-même (#295) : ni récolte ni arbre, `subjectId = 0`. */
    ACCOUNT,

    // back-office lot 1 (#333), in-app seulement
    SPECIES_SUGGESTION,

    // retour utilisateur (#586) et compte signalé (#588), in-app seulement
    FEEDBACK,
    USER_REPORT,
    UNKNOWN,
}

/**
 * Une notification in-app (#225 P0). [payload] porte des libellés déjà dénormalisés au moment de
 * l'écriture (`Map<String, String>` plutôt qu'un JSON arbitraire — le contrat public le plus simple
 * à consommer sur les trois plateformes, cf. #225 « choisir le plus simple sérialisable KMP »).
 * [createdAt]/[readAt] suivent la convention du contrat : epoch millis.
 *
 * [actorUserId] est `null` pour `TREE_DISPUTED` (ADR 0015/0017 : le signaleur reste anonyme), sinon
 * l'utilisateur dont le geste a déclenché la notification.
 *
 * [kind]/[subjectType] défaut à `UNKNOWN` (#253/#157, résilience client) : combiné à
 * `coerceInputValues` côté client, une valeur d'enum non reconnue devient `UNKNOWN` plutôt que de
 * faire échouer tout le décodage de la liste — le backend, lui, n'écrit jamais cette valeur.
 */
@Serializable
data class NotificationDto(
    val id: Long,
    val kind: NotificationKindDto = NotificationKindDto.UNKNOWN,
    val subjectType: NotificationSubjectTypeDto = NotificationSubjectTypeDto.UNKNOWN,
    val subjectId: Long,
    val actorUserId: Long? = null,
    val payload: Map<String, String> = emptyMap(),
    @Serializable(with = InstantEpochMillisSerializer::class)
    val createdAt: Instant,
    @Serializable(with = InstantEpochMillisSerializer::class)
    val readAt: Instant? = null,
)

/**
 * Enveloppe de `GET /api/v1/notifications` (#225 P0). [unreadCount] est rendu à chaque appel — même si
 * la page demandée ne couvre que des notifications lues — pour que le badge n'ait jamais besoin
 * d'un second aller-retour. [nextCursor] est l'`id` à passer en `beforeId` pour la page suivante ;
 * `null` quand la page rendue est la dernière.
 */
@Serializable
data class NotificationListResponse(
    val notifications: List<NotificationDto>,
    val unreadCount: Int,
    val nextCursor: Long? = null,
)

/** Corps de `POST /api/v1/notifications/read` (#225 P0) : soit une liste d'[ids], soit [all] = true —
 *  jamais les deux à la fois côté service (`all` prend le pas, cf. `NotificationService.markRead`). */
@Serializable
data class MarkNotificationsReadRequest(
    val ids: List<Long> = emptyList(),
    val all: Boolean = false,
)

/**
 * Préférences de notification (#225 P0, ADR 0017 D9). [inAppEnabled] est rendu pour la complétude
 * de la lecture mais n'est jamais modifiable — `PUT` l'ignore silencieusement (cf. `NotificationRoutes`).
 */
@Serializable
data class NotificationPreferencesDto(
    val inAppEnabled: Boolean = true,
    val emailEnabled: Boolean = false,
    val pushEnabled: Boolean = false,
    val harvestEvents: Boolean = true,
    val treeEvents: Boolean = true,
    val maturityAlerts: Boolean = true,
    /** Vrai si `PREMIUM_ENFORCED` est actif et que le compte n'a pas de Frux+ actif (#455) : la
     *  préférence se stocke mais n'a pas d'effet. Défaut faux pour les clients déjà publiés. */
    val maturityAlertsLocked: Boolean = false,
)

/** Corps de `PUT /api/v1/me/notification-preferences` (#225 P0) — même forme que la lecture,
 *  `inAppEnabled` compris (ignoré à l'écriture, cf. [NotificationPreferencesDto]). */
@Serializable
data class UpdateNotificationPreferencesRequest(
    val emailEnabled: Boolean = false,
    val pushEnabled: Boolean = false,
    val harvestEvents: Boolean = true,
    val treeEvents: Boolean = true,
    val maturityAlerts: Boolean = true,
)

/** Plateformes de jeton push (#252 P2, V24). `IOS`/`WEB` existent dans le schéma dès maintenant :
 *  le contrat n'aura pas à bouger au jalon M4 (ADR 0003), seuls les clients manqueront. */
@Serializable
enum class PushPlatformDto {
    ANDROID,
    IOS,
    WEB,
}

/**
 * Corps de `POST /api/v1/me/push-tokens` (#252 P2, ADR 0017). [token] est le jeton d'appareil du
 * fournisseur (FCM sur Android) : il n'identifie pas la personne, il adresse un appareil.
 */
@Serializable
data class RegisterPushTokenRequest(
    val token: String,
    val platform: PushPlatformDto,
)

/** Corps de `DELETE /api/v1/me/push-tokens` (#252 P2) : appelé à la déconnexion, avant l'invalidation
 *  de session — il faut donc encore un jeton valide pour l'émettre. */
@Serializable
data class RevokePushTokenRequest(
    val token: String,
)
